import authStore from '../store/auth';
import { useUserGroupsCurrentUserAdminState } from '../services/user.service';

// Legacy hardcoded flags must be registered here (used by FEATURE_TOGGLES_DATA).
// Company-managed flags (created via admin center) work automatically via
// useFeatureFlag(key) and useCompanyFeatures() — registering them here is
// optional and only needed if you want a named constant instead of a raw string.
export const FEATURE_FLAG_NAMES: Record<string, string> = {
  TEST_GLOBAL_FLAG: 'TEST_GLOBAL_FLAG',
  TEST_GROUP_FLAG: 'TEST_GROUP_FLAG',
  TEST_EMAIL_FLAG: 'TEST_EMAIL_FLAG',
  FORM_PF: 'form_pf',
  DATOX_COWORK: 'datox_cowork',
  AI_REPORT_REVIEW: 'ai_report_review',
  FILE_UPLOAD_CLOUD_INTEGRATION: 'file_upload_cloud_integration',
  // Company-managed flag (admin center). Gates the Lookup Tables tab on
  // the Extraction page + the Lookup tab in the Join transformation drawer.
  LOOKUP_TABLE: 'lookup_table'
};

interface FeatureToggle {
  isEnabledGlobally: boolean;
  enabledUserGroups: string[];
  enabledUserEmails: string[];
}

type AuthStore = Record<string, any>;

export const FEATURE_TOGGLES_DATA: Record<string, FeatureToggle> = {
  [FEATURE_FLAG_NAMES.TEST_GLOBAL_FLAG]: {
    isEnabledGlobally: true,
    enabledUserGroups: [],
    enabledUserEmails: []
  },
  [FEATURE_FLAG_NAMES.TEST_GROUP_FLAG]: {
    isEnabledGlobally: false,
    enabledUserGroups: ['test-group-id-1', 'test-group-id-2'],
    enabledUserEmails: []
  },
  [FEATURE_FLAG_NAMES.TEST_EMAIL_FLAG]: {
    isEnabledGlobally: false,
    enabledUserGroups: [],
    enabledUserEmails: ['ismoil@datox.ai']
  }
};

/**
 * Hook to check if a feature flag is enabled.
 *
 * Reads features from the GET /api/user-groups/user response (React Query cache).
 * Falls back to legacy FEATURE_TOGGLES_DATA for hardcoded flags.
 */
export const useFeatureFlag = (flagName: string): boolean => {
  const { data } = useUserGroupsCurrentUserAdminState() as { data: any };
  const features: Record<string, boolean> = data?.features || {};
  if (flagName in features) {
    return features[flagName] === true;
  }

  // Legacy hardcoded fallback
  const featureToggle = FEATURE_TOGGLES_DATA[flagName];
  if (!featureToggle) return false;
  if (featureToggle.isEnabledGlobally) return true;

  const store = authStore as AuthStore;
  const userEmail = store?.user?.username as string | undefined;
  if (userEmail && featureToggle.enabledUserEmails?.includes(userEmail)) return true;

  return false;
};

/**
 * Returns the full features map for the current user's company.
 * Source: GET /api/user-groups/user → response.features
 */
export const useCompanyFeatures = (): Record<string, boolean> => {
  const { data } = useUserGroupsCurrentUserAdminState() as { data: any };
  return (data?.features as Record<string, boolean>) || {};
};
